import React, { useMemo, useState } from 'react';
import {
  Download,
  Calendar,
  ListChecks,
  CalendarCheck,
  CheckCircle2,
  XCircle,
  DollarSign,
  TrendingUp,
  Wallet,
  Users,
  UserPlus,
  RefreshCw,
  Package,
  FileBarChart2,
} from 'lucide-react';
import { useVendorReports } from '../../services/vendorAnalyticsService';
import CustomSelect from '../../components/shared/CustomSelect';
import { VendorPageHeader, VendorGuideCallout } from '../../components/vendor/guidance';
import { VENDOR_PAGE_GUIDES } from '../../utils/vendorPageGuides';

const REPORT_OPTIONS = [
  { value: 'bookings', label: 'Bookings' },
  { value: 'revenue', label: 'Revenue' },
  { value: 'clients', label: 'Clients' },
  { value: 'services', label: 'Services' },
];

const PERIOD_OPTIONS = [
  { value: 'week', label: 'This week' },
  { value: 'month', label: 'This month' },
  { value: 'quarter', label: 'This quarter' },
  { value: 'year', label: 'This year' },
];

const PERIOD_LABELS = {
  week: 'Week',
  month: 'Month',
  quarter: 'Quarter',
  year: 'Year',
};

const REPORT_LABELS = {
  bookings: 'Bookings',
  revenue: 'Revenue',
  clients: 'Clients',
  services: 'Services',
};

function MetricCard({ icon: Icon, label, value, tone = 'neutral' }) {
  const tones = {
    neutral: {
      wrap: 'border-gray-200 dark:border-gray-700 bg-gray-50/80 dark:bg-gray-950/40',
      icon: 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 text-gray-600 dark:text-gray-300',
      value: 'text-gray-900 dark:text-gray-50',
    },
    success: {
      wrap: 'border-emerald-200/80 dark:border-emerald-800/50 bg-emerald-50/50 dark:bg-emerald-950/20',
      icon: 'border-emerald-200 dark:border-emerald-800 bg-white dark:bg-emerald-950/40 text-emerald-600 dark:text-emerald-400',
      value: 'text-emerald-700 dark:text-emerald-300',
    },
    danger: {
      wrap: 'border-red-200/80 dark:border-red-800/50 bg-red-50/50 dark:bg-red-950/20',
      icon: 'border-red-200 dark:border-red-800 bg-white dark:bg-red-950/40 text-red-600 dark:text-red-400',
      value: 'text-red-700 dark:text-red-300',
    },
    info: {
      wrap: 'border-sky-200/80 dark:border-sky-800/50 bg-sky-50/50 dark:bg-sky-950/20',
      icon: 'border-sky-200 dark:border-sky-800 bg-white dark:bg-sky-950/40 text-sky-600 dark:text-sky-400',
      value: 'text-sky-700 dark:text-sky-300',
    },
  };
  const t = tones[tone] || tones.neutral;

  return (
    <div className={`rounded-xl border p-4 sm:p-5 ${t.wrap}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="text-[11px] font-semibold uppercase tracking-[0.12em] text-gray-500 dark:text-gray-400">
            {label}
          </p>
          <p className={`mt-2 text-2xl sm:text-3xl font-bold tracking-tight tabular-nums ${t.value}`}>
            {value}
          </p>
        </div>
        <div
          className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border ${t.icon}`}
        >
          <Icon className="h-4 w-4" />
        </div>
      </div>
    </div>
  );
}

const VendorReports = () => {
  const [reportType, setReportType] = useState('bookings');
  const [period, setPeriod] = useState('month');

  const { data, isLoading, error } = useVendorReports(reportType, period);
  const reports = data?.data || {};

  const title = useMemo(
    () => `${REPORT_LABELS[reportType] || 'Report'} · ${PERIOD_LABELS[period] || period}`,
    [reportType, period]
  );

  const handleDownload = () => {
    const payload = {
      type: reportType,
      period,
      generated_at: new Date().toISOString(),
      data: reports,
    };
    const blob = new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `vendalist-${reportType}-${period}-report.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const renderMetrics = () => {
    if (reportType === 'bookings') {
      return (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4">
          <MetricCard
            icon={CalendarCheck}
            label="Total bookings"
            value={reports.total_bookings || 0}
          />
          <MetricCard
            icon={CheckCircle2}
            label="Completed"
            value={reports.completed_bookings || 0}
            tone="success"
          />
          <MetricCard
            icon={XCircle}
            label="Cancelled"
            value={reports.cancelled_bookings || 0}
            tone="danger"
          />
        </div>
      );
    }

    if (reportType === 'revenue') {
      return (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4">
          <MetricCard
            icon={DollarSign}
            label="Total revenue"
            value={`$${Number(reports.total_revenue || 0).toFixed(2)}`}
          />
          <MetricCard
            icon={TrendingUp}
            label="Average booking"
            value={`$${Number(reports.average_booking || 0).toFixed(2)}`}
            tone="info"
          />
          <MetricCard
            icon={Wallet}
            label="Net earnings"
            value={`$${Number(reports.net_earnings || 0).toFixed(2)}`}
            tone="success"
          />
        </div>
      );
    }

    if (reportType === 'clients') {
      return (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4">
          <MetricCard icon={Users} label="Total clients" value={reports.total_clients || 0} />
          <MetricCard
            icon={UserPlus}
            label="New clients"
            value={reports.new_clients || 0}
            tone="info"
          />
          <MetricCard
            icon={RefreshCw}
            label="Repeat clients"
            value={reports.repeat_clients || 0}
            tone="success"
          />
        </div>
      );
    }

    const topServices = reports.top_services || [];
    if (!topServices.length) {
      return (
        <div className="rounded-xl border border-dashed border-gray-300 dark:border-gray-600 bg-gray-50/50 dark:bg-gray-950/30 px-4 py-10 text-center">
          <Package className="mx-auto h-8 w-8 text-gray-300 dark:text-gray-600 mb-2" />
          <p className="text-sm font-medium text-gray-700 dark:text-gray-300">No service data yet</p>
          <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
            Service performance will appear here once you have bookings.
          </p>
        </div>
      );
    }

    return (
      <div className="space-y-2.5">
        {topServices.map((service, index) => (
          <div
            key={`${service.name}-${index}`}
            className="flex items-center justify-between gap-4 rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50/60 dark:bg-gray-950/40 px-4 py-3.5"
          >
            <div className="flex items-center gap-3 min-w-0">
              <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 text-xs font-semibold text-gray-500 dark:text-gray-400 tabular-nums">
                {index + 1}
              </span>
              <div className="min-w-0">
                <p className="text-sm font-semibold text-gray-900 dark:text-gray-100 truncate">
                  {service.name}
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  {service.bookings_count || 0} bookings
                </p>
              </div>
            </div>
            <p className="text-sm font-semibold text-gray-900 dark:text-gray-100 tabular-nums shrink-0">
              ${Number(service.revenue || 0).toFixed(2)}
            </p>
          </div>
        ))}
      </div>
    );
  };

  return (
    <div className="space-y-5">
      <VendorPageHeader
        title="Reports"
        subtitle="Generate summary reports for your business and export them for your records."
      />

      <VendorGuideCallout
        title={VENDOR_PAGE_GUIDES.reports.title}
        steps={VENDOR_PAGE_GUIDES.reports.steps}
        icon={ListChecks}
      />

      <div className="rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 shadow-sm overflow-hidden">
        <div className="flex flex-col gap-4 border-b border-gray-200 dark:border-gray-800 px-4 py-4 sm:px-5 sm:py-4 lg:flex-row lg:items-center lg:justify-between">
          <div className="flex items-start gap-3 min-w-0">
            <div className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 text-gray-700 dark:text-gray-200">
              <FileBarChart2 className="h-4 w-4" />
            </div>
            <div className="min-w-0">
              <h2 className="text-base font-semibold tracking-tight text-gray-900 dark:text-gray-50">
                {title}
              </h2>
              <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">
                Choose a report type and period, then download a copy for your records.
              </p>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-2 sm:items-center shrink-0">
            <div className="w-full sm:w-40">
              <CustomSelect
                value={reportType}
                onChange={setReportType}
                options={REPORT_OPTIONS}
                size="sm"
                className="w-full"
              />
            </div>
            <div className="w-full sm:w-40">
              <CustomSelect
                value={period}
                onChange={setPeriod}
                options={PERIOD_OPTIONS}
                size="sm"
                className="w-full"
              />
            </div>
            <button
              type="button"
              onClick={handleDownload}
              disabled={isLoading || Boolean(error)}
              className="inline-flex items-center justify-center gap-2 rounded-xl bg-gray-900 dark:bg-gray-100 px-4 py-2.5 text-sm font-medium text-white dark:text-gray-900 hover:bg-black dark:hover:bg-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
            >
              <Download className="h-4 w-4" />
              Download
            </button>
          </div>
        </div>

        <div className="p-4 sm:p-5">
          {isLoading ? (
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4 animate-pulse">
              {[1, 2, 3].map((i) => (
                <div
                  key={i}
                  className="rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-950/40 p-5"
                >
                  <div className="h-3 w-24 rounded bg-gray-200 dark:bg-gray-800 mb-4" />
                  <div className="h-8 w-16 rounded bg-gray-200 dark:bg-gray-800" />
                </div>
              ))}
            </div>
          ) : error ? (
            <div className="rounded-xl border border-red-200 dark:border-red-800/50 bg-red-50/80 dark:bg-red-950/30 px-4 py-3 text-sm text-red-700 dark:text-red-300">
              Failed to load reports. Please try again.
            </div>
          ) : (
            renderMetrics()
          )}
        </div>

        <div className="border-t border-gray-100 dark:border-gray-800 px-4 py-3 sm:px-5 bg-gray-50/60 dark:bg-gray-950/40">
          <p className="text-xs text-gray-500 dark:text-gray-400 inline-flex items-center gap-1.5">
            <Calendar className="h-3.5 w-3.5" />
            Figures update when you change report type or period.
          </p>
        </div>
      </div>
    </div>
  );
};

export default VendorReports;
